import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Home,
  Settings,
  Users,
  Building2,
  Shield,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  CircleUserRound,
} from 'lucide-react';
import { Separator } from '../../../@/components/ui/separator';
import HainokLogo from '/hainok_full_logo.png';
import InmopublicLogo from '/inmopublic_full_logo.png';

type NavigationItem = {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
};

const hainokNavigationItems: NavigationItem[] = [
  {
    label: 'Dashboard',
    href: '/dashboard',
    icon: Home,
  },
  {
    label: 'Accounts',
    href: '/accounts',
    icon: Building2,
  },
  {
    label: 'Users',
    href: '/users',
    icon: Users,
  },
  {
    label: 'Authenticator',
    href: '/authenticator',
    icon: Shield,
  },
];

const inmopublicNavigationItems: NavigationItem[] = [
  {
    label: 'Users',
    href: '/inmopublic/users',
    icon: Users,
  },
  {
    label: 'Accounts',
    href: '/inmopublic/accounts',
    icon: Building2,
  },
  {
    label: 'Memberships',
    href: '/inmopublic/memberships',
    icon: CircleUserRound,
  },
];

const commonNavigationItems: NavigationItem[] = [
  {
    label: 'Settings',
    href: '/settings',
    icon: Settings,
  },
];

export const Navigation = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleMouseEnter = () => {
    if (window.innerWidth >= 768) {
      setIsExpanded(true);
    }
  };

  const handleMouseLeave = () => {
    if (window.innerWidth >= 768) {
      setIsExpanded(false);
    }
  };

  const handleNavigation = (href: string) => {
    navigate(href);
    if (window.innerWidth < 768) {
      setIsMobileOpen(false);
    }
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Mobile menu button
  const MobileMenuButton = () => (
    <Button
      variant='ghost'
      size='sm'
      className='fixed top-4 left-4 z-50 md:hidden bg-slate-800/90 backdrop-blur-sm text-white hover:bg-slate-700 border border-slate-600'
      onClick={() => setIsMobileOpen(!isMobileOpen)}
    >
      {isMobileOpen ? <X className='h-5 w-5' /> : <Menu className='h-5 w-5' />}
    </Button>
  );

  return (
    <>
      <MobileMenuButton />

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className='fixed inset-0 bg-black/60 z-40 md:hidden backdrop-blur-sm'
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Desktop Navigation */}
      <nav
        className={cn(
          'fixed left-0 top-0 h-full bg-slate-800 border-r border-slate-700 transition-all duration-300 ease-in-out z-30',
          'hidden md:block',
          isExpanded ? 'w-64' : 'w-16',
        )}
        style={{ paddingTop: 0.25 + 'rem' }} // Adjust for fixed header height
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className='flex flex-col h-full'>
          {/* Toggle Button - Desktop only */}
          <div className='p-4 border-b border-slate-700'>
            <Button
              variant='ghost'
              size='sm'
              className='w-full justify-center text-slate-400 hover:text-white hover:bg-slate-700'
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? <ChevronLeft className='h-4 w-4' /> : <ChevronRight className='h-4 w-4' />}
            </Button>
          </div>

          {/* Navigation Items */}
          <div className='flex-1 py-4 overflow-y-auto'>
            <img src={HainokLogo} alt='Hainok Logo' className={cn('mx-auto mb-4', isExpanded ? 'w-32' : 'w-8')} />
            <ul className='space-y-2 px-3'>
              {hainokNavigationItems.map((item) => {
                const isActive = location.pathname === item.href;
                const Icon = item.icon;

                return (
                  <li key={item.href}>
                    <Button
                      variant='ghost'
                      className={cn(
                        'w-full justify-start text-slate-400 hover:text-white hover:bg-slate-700 transition-colors transition-display duration-300 ease-in-out z-30',
                        isActive && 'bg-slate-700 text-white justify-start',
                        isExpanded ? 'justify-start' : 'flex justify-center',
                      )}
                      onClick={() => handleNavigation(item.href)}
                    >
                      <Icon className={cn('h-5 w-5', isExpanded && 'mr-3')} />
                      {isExpanded && <span className='truncate'>{item.label}</span>}
                    </Button>
                  </li>
                );
              })}
            </ul>
            <Separator className='my-4 bg-slate-700' />
            <img
              src={InmopublicLogo}
              alt='Inmopublic Logo'
              className={cn('mx-auto mb-4', isExpanded ? 'w-32' : 'w-8')}
            />
            <ul className='space-y-2 px-3 mt-2'>
              {inmopublicNavigationItems.map((item) => {
                const isActive = location.pathname === item.href;
                const Icon = item.icon;

                return (
                  <li key={item.href}>
                    <Button
                      variant='ghost'
                      className={cn(
                        'w-full justify-start text-slate-400 hover:text-white hover:bg-slate-700 transition-colors transition-display duration-300 ease-in-out z-30',
                        isActive && 'bg-slate-700 text-white justify-start',
                        isExpanded ? 'justify-start' : 'flex justify-center',
                      )}
                      onClick={() => handleNavigation(item.href)}
                    >
                      <Icon className={cn('h-5 w-5', isExpanded && 'mr-3')} />
                      {isExpanded && <span className='truncate'>{item.label}</span>}
                    </Button>
                  </li>
                );
              })}
            </ul>
            <Separator className='my-4 bg-slate-700' />
            <ul className='space-y-2 px-3 mt-2'>
              {commonNavigationItems.map((item) => {
                const isActive = location.pathname === item.href;
                const Icon = item.icon;

                return (
                  <li key={item.href}>
                    <Button
                      variant='ghost'
                      className={cn(
                        'w-full justify-start text-slate-400 hover:text-white hover:bg-slate-700 transition-colors transition-display duration-300 ease-in-out z-30',
                        isActive && 'bg-slate-700 text-white justify-start',
                        isExpanded ? 'justify-start' : 'flex justify-center',
                      )}
                      onClick={() => handleNavigation(item.href)}
                    >
                      <Icon className={cn('h-5 w-5', isExpanded && 'mr-3')} />
                      {isExpanded && <span className='truncate'>{item.label}</span>}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Footer */}
          {isExpanded && (
            <div className='p-4 border-t border-slate-700'>
              <p className='text-xs text-slate-500 text-center'>Hainok Admin v1.1</p>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav
        className={cn(
          'fixed top-0 left-0 h-full bg-slate-800 border-r border-slate-700 transition-transform duration-300 ease-in-out z-50 md:hidden',
          'w-72 max-w-[80vw]',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className='flex flex-col h-full'>
          {/* Mobile Header */}
          <div className='p-6 border-b border-slate-700'>
            <h2 className='text-xl font-bold text-white'>Navigation</h2>
          </div>

          {/* Navigation Items */}
          <div className='flex-1 py-4 overflow-y-auto'>
            <ul className='space-y-2 px-4'>
              {hainokNavigationItems.map((item) => {
                const isActive = location.pathname === item.href;
                const Icon = item.icon;

                return (
                  <li key={item.href}>
                    <Button
                      variant='ghost'
                      className={cn(
                        'w-full justify-start text-slate-400 hover:text-white hover:bg-slate-700 transition-colors py-3 h-auto',
                        isActive && 'bg-slate-700 text-white',
                      )}
                      onClick={() => handleNavigation(item.href)}
                    >
                      <Icon className='h-5 w-5 mr-3 flex-shrink-0' />
                      <span className='text-base'>{item.label}</span>
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Mobile Footer */}
          <div className='p-4 border-t border-slate-700'>
            <p className='text-xs text-slate-500 text-center'>Hainok Admin v1.1</p>
          </div>
        </div>
      </nav>
    </>
  );
};
